import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface MqttUser {
  id: string;
  username: string;
  active: boolean;
  acls: string[];
  tenantId: string;
  lastSeenAt: string | null;
  createdAt: string;
}

// Constantes de despliegue — no vienen de ningún endpoint, son las mismas
// para todo gateway (ver mosquitto/config/mosquitto.conf + docker-compose.yml).
const BROKER_HOST = 'app.dimasfarming.com';
const BROKER_PORT = 1883;
const DEFAULT_TOPICS = ['telemetry/data/#', 'telemetry/command/#', 'telemetry/status/#'];

/**
 * Los topics de arriba llevan `#`, o sea que la credencial puede publicar como
 * CUALQUIER imei — incluido el sensor de otro cliente. Sirve para un gateway
 * que agrupa varios equipos, pero para un sensor suelto es permiso de más: si
 * esas credenciales se filtran, quien las tenga puede inyectar telemetría a
 * nombre de cualquier device de la plataforma.
 *
 * Con un imei, los mismos tres topics quedan acotados a ese equipo y nada más.
 * El matcher del broker (apps/core/src/modules/mqtt-internal/routes.ts) compara
 * el topic contra estos patrones, así que un topic exacto funciona igual que
 * uno con comodín.
 */
function topicsForImei(imei: string): string[] {
  return [`telemetry/data/${imei}`, `telemetry/command/${imei}`, `telemetry/status/${imei}`];
}

export default function ClientMqttTab({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const { t } = useTranslation();
  const [users, setUsers]     = useState<MqttUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [reveal, setReveal]   = useState<{ username: string; password: string } | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      // GET /mqtt-users no filtra por tenant server-side cuando lo llama
      // super_admin (devuelve todos) — mismo criterio que ClientDomainsTab/
      // ClientDevicesTab: filtrar client-side, el volumen total es bajo.
      const { data } = await api.get('/mqtt-users');
      setUsers((data as MqttUser[]).filter(u => u.tenantId === tenantId));
    } catch {
      setError(t('admin.mqtt.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  const deactivate = async (u: MqttUser) => {
    if (!window.confirm(t('admin.mqtt.confirmDeactivate'))) return;
    await api.patch(`/mqtt-users/${u.id}`, { active: false });
    load();
  };

  // Borrado permanente, SOLO para credenciales ya desactivadas. Borrar una
  // activa cortaría la conexión del dispositivo en el acto, y como el password
  // se guarda hasheado no hay forma de recrearla igual: habría que generar una
  // nueva y pedirle al proveedor que reconfigure el equipo. Desactivar primero
  // deja ver si algo se rompe, con vuelta atrás.
  const remove = async (u: MqttUser) => {
    if (!window.confirm(t('admin.mqtt.confirmDelete', { username: u.username }))) return;
    try {
      await api.delete(`/mqtt-users/${u.id}`);
      load();
    } catch {
      setError(t('admin.mqtt.deleteError'));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>{t('admin.mqtt.title', { tenant: tenantName })}</h2>
        <button
          onClick={() => setShowForm(true)}
          style={{ padding: '7px 16px', background: 'var(--dark)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
        >
          {t('admin.mqtt.addNew')}
        </button>
      </div>
      <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 16 }}>{t('admin.mqtt.subtitle')}</p>

      {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>...</div>
      ) : users.length === 0 ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>{t('admin.mqtt.noGateways')}</div>
      ) : (
        <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {users.map((u, i) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>{u.username}</div>
                <div style={{ fontSize: 11, color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.acls.join(', ')}</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: u.active ? 'var(--green-bg)' : '#f5f5f3', color: u.active ? 'var(--green-dk)' : 'var(--gray)', flexShrink: 0 }}>
                {u.active ? t('status.active') : t('status.inactive')}
              </span>
              {u.active ? (
                <button
                  onClick={() => deactivate(u)}
                  style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer', flexShrink: 0 }}
                >
                  {t('admin.mqtt.deactivate')}
                </button>
              ) : (
                <button
                  onClick={() => remove(u)}
                  style={{ fontSize: 11, padding: '5px 10px', borderRadius: 6, border: '1px solid var(--red, #c0392b)', background: 'transparent', color: 'var(--red, #c0392b)', cursor: 'pointer', flexShrink: 0, fontWeight: 600 }}
                >
                  {t('admin.mqtt.delete')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <AddMqttGatewayModal
          tenantId={tenantId}
          onClose={() => setShowForm(false)}
          onSaved={(username, password) => { setShowForm(false); setReveal({ username, password }); load(); }}
        />
      )}

      {reveal && (
        <MqttConnectionInfoModal
          username={reveal.username}
          password={reveal.password}
          onClose={() => setReveal(null)}
        />
      )}
    </div>
  );
}

function AddMqttGatewayModal({ tenantId, onClose, onSaved }: {
  tenantId: string; onClose: () => void; onSaved: (username: string, password: string) => void;
}) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [topics, setTopics]     = useState<string[]>(DEFAULT_TOPICS);
  const [imei, setImei]         = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const toggleTopic = (topic: string) => {
    setTopics(t => t.includes(topic) ? t.filter(x => x !== topic) : [...t, topic]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      // Con imei gana el alcance acotado; sin imei, los comodines marcados.
      const allowedTopics = imei.trim() ? topicsForImei(imei.trim()) : topics;
      await api.post('/mqtt-users', { username: username.trim(), password, tenantId, allowedTopics });
      onSaved(username.trim(), password);
    } catch (err: any) {
      setError(err.response?.status === 409 ? t('admin.mqtt.duplicateError') : (err.response?.data?.error ?? t('admin.mqtt.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--white)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>{t('admin.mqtt.addModal.title')}</h2>
        <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 20 }}>{t('admin.mqtt.addModal.subtitle')}</p>

        {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label>{t('admin.mqtt.addModal.username')}</label>
            <input value={username} onChange={e => setUsername(e.target.value)} required minLength={3} placeholder={t('admin.mqtt.addModal.usernamePlaceholder')} />
          </div>
          <div className="field">
            <label>{t('admin.mqtt.addModal.password')}</label>
            <input type="text" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} placeholder={t('admin.mqtt.addModal.passwordPlaceholder')} />
          </div>
          <div className="field">
            <label>{t('admin.mqtt.addModal.imei')}</label>
            <input
              value={imei}
              onChange={e => setImei(e.target.value.replace(/\D/g, ''))}
              maxLength={64}
              inputMode="numeric"
              placeholder={t('admin.mqtt.addModal.imeiPlaceholder')}
            />
            <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 4, lineHeight: 1.5 }}>
              {t('admin.mqtt.addModal.imeiHelp')}
            </div>
          </div>
          <div className="field">
            <label>{t('admin.mqtt.addModal.topics')}</label>
            {imei.trim() ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                {topicsForImei(imei.trim()).map(topic => (
                  <span key={topic} style={{ fontSize: 12, fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace', color: 'var(--green-dk)' }}>
                    {topic}
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                {DEFAULT_TOPICS.map(topic => (
                  <label key={topic} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--dark)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={topics.includes(topic)} onChange={() => toggleTopic(topic)} />
                    <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>{topic}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', fontSize: 13, cursor: 'pointer' }}>
              {t('tenants.form.cancel')}
            </button>
            <button type="submit" disabled={saving || (!imei.trim() && topics.length === 0)} className="primary-button" style={{ flex: 1 }}>
              {saving ? t('admin.mqtt.addModal.saving') : t('admin.mqtt.addModal.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MqttConnectionInfoModal({ username, password, onClose }: { username: string; password: string; onClose: () => void }) {
  const { t } = useTranslation();
  const rows: [string, string][] = [
    ['MQTT_HOST', BROKER_HOST],
    ['MQTT_PORT', String(BROKER_PORT)],
    ['MQTT_USER', username],
    ['MQTT_PASSWORD', password],
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--white)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>{t('admin.mqtt.reveal.title')}</h2>
        <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 16 }}>{t('admin.mqtt.reveal.subtitle')}</p>

        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', fontSize: 13, lineHeight: 1.9, fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>
          {rows.map(([k, v]) => (
            <div key={k}>{k}=<span style={{ fontWeight: 600 }}>{v}</span></div>
          ))}
        </div>

        <div className="error-message" style={{ marginTop: 14, marginBottom: 0 }}>{t('admin.mqtt.reveal.warning')}</div>

        <button
          onClick={onClose}
          className="primary-button"
          style={{ width: '100%', marginTop: 20 }}
        >
          {t('admin.mqtt.reveal.done')}
        </button>
      </div>
    </div>
  );
}
